import React from "react";
import useFetch from "v4/hooks/useFetch";
import {useCallback, useEffect, useMemo, useState} from "react";
import {generateUrl} from "v4/services/api.service";
import {IMPORT_MAPPING_FORM} from "v4/data/apiRoutes";
import {useParams} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useImportContext} from "v4/pages/admin/Import/contexts/ImportContext";
import useNotification from "v4/hooks/useNotification";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import useAutoFetch from "v4/hooks/useAutoFetch";
import useToggle from "v4/hooks/useToggle";
import {components as ReactSelectComponents} from "react-select";
import ReactTooltip from "react-tooltip";

const ControlWithTitle = ({children, getValue, ...props}) => {
    const label = getValue()[0]?.label ?? '';
    const tooltipId = `select-tooltip-${props.selectProps.name}`;
    return (
        <ReactSelectComponents.Control {...props} innerProps={{...props.innerProps, 'data-tip': label, 'data-for': tooltipId}}>
            {children}
            {label && <ReactTooltip id={tooltipId} effect="solid" place="top"/>}
        </ReactSelectComponents.Control>
    );
};

export default function useMapImportForm(tableHeaders) {
    const {t} = usePolTranslation();
    const {nextStep} = useImportContext();
    const {id: importId} = useParams();
    const {data: form, isLoading} = useAutoFetch(generateUrl(IMPORT_MAPPING_FORM, {importId}), importId);
    const [{data: saveMappingData, errorData, isLoading: isSaveLoading}, saveMapping] = useFetch();
    const formErrors = errorData?.results.errors;
    const methods = useForm();
    const identifierFormMethods = useForm();
    const {addSuccessNotification, addErrorNotification} = useNotification();

    const formInputsOnly = ({key}) => Object.keys(form?.form?.results ?? {}).includes(key);

    const onSubmit = (data) => {
        saveMapping({
            url: generateUrl(IMPORT_MAPPING_FORM, {importId}),
            config: {
                method: 'POST',
                body: JSON.stringify(data),
            }
        });
    }

    useEffect(() => {
        if (!errorData?.messages) return;

        // eslint-disable-next-line no-unused-expressions
        errorData.messages?.errors?.forEach((error) => {
            addErrorNotification(t(error));
        });
    }, [errorData, addErrorNotification, addSuccessNotification, t]);

    useEffect(() => {
        if (!formErrors) return;

        formErrors.filter(formInputsOnly).forEach(({key, messages}) => {
            methods.setError(key, {
                type: 'manual',
                message: messages.map(m => t(m)).join(' '),
            });
        });

        const generalErrors = formErrors.find(({key}) => key === 'general_error');
        if (generalErrors) {
            addErrorNotification(generalErrors.messages.map((message) => t(message)).join(' '));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formErrors]);

    useEffect(() => {
        if (saveMappingData?.import.state !== 'validated') return;

        nextStep(saveMappingData.import.id);
    }, [nextStep, saveMappingData]);

    const [isConfirmOpen, toggleConfirm] = useToggle();
    const [selectAndValueToReplace, setSelectAndValueToReplace] = useState(null);
    const finalForm = useMemo(() => {
        if (!form?.form) return null;

        const fields = Object.values(form?.form?.results ?? {}).filter(field => !field.name.includes('_action'));
        fields.forEach(field => {
            if (!field?.choices) return;

            field.specificParams = {
                menuPosition: 'fixed',
                components: {Control: ControlWithTitle},
                onChange(data, handleChange) {
                    const valuesEntries = Object.entries(methods.getValues())
                        .filter(([name, value]) => !name.includes('_action') && Boolean(value) && name !== field.name);
                    const valuesOnly = valuesEntries.map(([, value]) => value);

                    // If the selected value is already used, ask if we want to replace it
                    if (valuesOnly.includes(data?.value)) {
                        const columnKey = valuesEntries.find(([, value]) => value === data.value)?.[0];
                        const columnName = tableHeaders.find(header => header.key === columnKey)?.label;

                        setSelectAndValueToReplace({
                            select: field.name,
                            value: data.value,
                            columnName,
                            handleChange
                        });
                        toggleConfirm();
                        return;
                    }

                    handleChange(data);
                },
                overrideStyles(selectStyle) {
                    return {
                        ...selectStyle,
                        //
                        option: (styles, option) => {
                            const previousStyles = selectStyle.option(styles, option);
                            const currentValue = option.getValue()[0]?.value;
                            const valuesEntries = Object.entries(methods.getValues())
                                .filter(([name, value]) => !name.includes('_action') && Boolean(value) && value !== currentValue);
                            const valuesOnly = valuesEntries.map(([, value]) => value);
                            const isOptionAlreadyUsed = valuesOnly.includes(option.data.value);

                            // This function is supposed only handle styles, but it's the easiest way to modify the
                            // label to add the column name when the option is already used
                            const arrow = '←';
                            const label = option.data.label.split(` ${arrow}`)[0];
                            if (isOptionAlreadyUsed && tableHeaders) {
                                // Get the label of the column that is using this value
                                const columnKey = valuesEntries.find(([, value]) => value === option.data.value)?.[0];
                                const columnName = tableHeaders.find(header => header.key === columnKey)?.label;
                                option.data.label = `${label} ${arrow} ${columnName ?? columnKey}`;
                            } else {
                                option.data.label = label;
                            }
                            // End of the label modification

                            return {
                                ...previousStyles,
                                backgroundColor: isOptionAlreadyUsed ? '#fff0d4' : previousStyles.backgroundColor,
                                color: isOptionAlreadyUsed ? '#000' : previousStyles.color,
                                whiteSpace: 'normal',
                                ':hover': {
                                    backgroundColor: isOptionAlreadyUsed ? '#ffe4b3' : previousStyles.backgroundColor,
                                    color: isOptionAlreadyUsed ? '#000' : previousStyles.color,
                                }
                            }
                        }
                    }
                }
            }
        });

        return form.form;
    }, [form, methods, tableHeaders, toggleConfirm]);

    const confirmAction = useCallback((confirm) => {
        if (confirm && selectAndValueToReplace) {
            // Find the field that has the value to replace
            const fieldToClear = Object.entries(methods.getValues())
                .find(([name, value]) => !name.includes('_action') && value === selectAndValueToReplace.value);
            if (fieldToClear) {
                methods.setValue(fieldToClear[0], '');
            }

            // Set the value to the selected field
            selectAndValueToReplace.handleChange({value: selectAndValueToReplace.value});
        }
        setSelectAndValueToReplace(null);
        toggleConfirm();
    }, [selectAndValueToReplace, methods, toggleConfirm]);

    return {
        form: finalForm,
        isFormLoading: isLoading,
        isSaveLoading,
        methods,
        identifierFormMethods,
        onSubmit,
        isConfirmOpen,
        columnNameToOverride: selectAndValueToReplace?.columnName,
        confirmAction,
        toggleConfirm,
    }
}
